import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Edit3, Save, Lock, Key, Copy, Trash2, Bell, X } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { apiGet, apiPatch, apiPost, apiDelete } from '@/shared/api';
import { getRoleColor } from '@/shared/lib/utils';
import AvatarUpload from '@/features/auth/components/AvatarUpload';
import { Button } from '@/shared/components/ui/Button';
import { useNotificationStore, type NotificationEventType } from '@/shared/stores/notification-store';
import { useProfileModalStore } from '@/shared/stores/profile-modal-store';
import { showConfirm } from '@/shared/stores/confirm-store';

interface ProfileData {
  user: { id: string; username: string; nickname: string; avatarUrl: string | null; githubId?: string | null; role?: string };
}

export default function ProfileModal() {
  const isOpen = useProfileModalStore((s) => s.isOpen);
  const close = useProfileModalStore((s) => s.close);

  const { user, setUser } = useAuthStore();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [editingNickname, setEditingNickname] = useState(false);
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [apiKeys, setApiKeys] = useState<{ id: string; name: string; keyPreview: string; createdAt: string }[]>([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyFull, setNewKeyFull] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [keyError, setKeyError] = useState('');
  const [keyCopied, setKeyCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    apiGet<ProfileData>('/profile').then((p) => {
      setProfile(p);
      setNickname(p.user.nickname);
    }).catch(() => close());
    apiGet<typeof apiKeys>('/api-keys').then(setApiKeys).catch(() => {});
  }, [isOpen]);

  const handleSaveNickname = async () => {
    setSaving(true);
    try {
      await apiPatch('/profile', { nickname });
      setProfile((p) => p ? { ...p, user: { ...p.user, nickname } } : p);
      if (user) setUser({ ...user, nickname });
      setEditingNickname(false);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (dataUrl: string) => {
    try {
      const res = await apiPost<{ avatarUrl: string | null }>('/profile/avatar', { avatar: dataUrl });
      const url = res.avatarUrl;
      setProfile((p) => p ? { ...p, user: { ...p.user, avatarUrl: url } } : p);
      if (user) setUser({ ...user, avatarUrl: url });
    } catch {
      // ignore
    }
  };

  const handleSetPassword = async () => {
    if (password.length < 8) { setPasswordMsg('Password must be at least 8 characters and must contain letters and numbers'); return; }
    if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) { setPasswordMsg('Password must contain both letters and numbers'); return; }
    if (password !== passwordConfirm) { setPasswordMsg('Two passwords are inconsistent'); return; }
    setPasswordMsg('');
    try {
      await apiPost('/auth/set-password', { password });
      setPasswordMsg('Password set successfully');
      setPassword('');
      setPasswordConfirm('');
    } catch (err) {
      setPasswordMsg((err as Error).message || 'Setup failed');
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;
    setCreatingKey(true);
    setKeyError('');
    try {
      const result = await apiPost<{ id: string; key: string; name: string; createdAt: string }>('/api-keys', { name: newKeyName.trim() });
      setNewKeyFull(result.key);
      setKeyCopied(false);
      setApiKeys((prev) => [{ id: result.id, name: result.name, keyPreview: `${result.key.slice(0, 11)}...`, createdAt: result.createdAt }, ...prev]);
      setNewKeyName('');
    } catch (err) {
      setKeyError((err as Error).message || 'Creation failed');
    } finally {
      setCreatingKey(false);
    }
  };

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKeyFull);
      setKeyCopied(true);
    } catch {
      setKeyError('Copy failed, please manually select text to copy');
    }
  };

  const handleDismissKey = () => {
    setNewKeyFull('');
    setKeyCopied(false);
  };

  const handleDeleteKey = async (id: string) => {
    if (!(await showConfirm({
      title: 'Delete API Key',
      message: 'MCP clients using this Key will not be able to connect after deletion, are you sure?',
      confirmText: 'delete',
      variant: 'danger',
    }))) return;
    try {
      await apiDelete(`/api-keys/${id}`);
      setApiKeys((prev) => prev.filter((k) => k.id !== id));
    } catch (err) {
      setKeyError((err as Error).message || 'Delete failed');
    }
  };

  const profileRoleColor = getRoleColor(profile?.user.role);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-modal glass-modal-backdrop"
            onClick={close}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-modal flex items-center justify-center pointer-events-none"
          >
            <div
              className="pointer-events-auto glass-panel w-[90vw] max-w-[860px] max-h-[85vh] flex overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Left: Profile card */}
              <div className="w-[240px] shrink-0 p-6 flex flex-col items-center gap-4 border-r border-white/5">
                {profile && (
                  <>
                    <AvatarUpload avatarUrl={profile.user.avatarUrl} size={96} onUpload={handleAvatarUpload} />

                    <div className="text-center">
                      {editingNickname ? (
                        <div className="flex items-center gap-2">
                          <input value={nickname} onChange={(e) => setNickname(e.target.value)}
                            className="glass-input w-32 text-sm" />
                          <Button variant="primary" size="sm" onClick={handleSaveNickname} disabled={saving} sound="click">
                            <Save size={14} />
                          </Button>
                        </div>
                      ) : (
                        <p className="text-xl font-bold cursor-pointer" onClick={() => setEditingNickname(true)}
                          style={profileRoleColor ? { color: profileRoleColor } : undefined}>
                          {profile.user.nickname} <Edit3 size={14} className="inline-block align-middle text-muted-foreground" />
                        </p>
                      )}
                      <p className="text-muted-foreground text-sm mt-1">@{profile.user.username}</p>
                    </div>
                  </>
                )}

                <div className="flex-1" />

                <Button variant="ghost" className="w-full" onClick={close} sound="click">closure</Button>
              </div>

              {/* Right: Settings */}
              <div className="flex-1 p-6 overflow-y-auto scrollbar-thin">
                {/* Notification settings */}
                <NotificationSettingsInline />

                <div className="border-b border-white/5 my-5" />

                {/* Password section */}
                <h3 className="text-sm text-muted-foreground mb-3 flex items-center gap-1.5">
                  <Lock size={14} /> Set password</h3>
                <div className="flex flex-col gap-2">
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New Password"
                    className="glass-input w-full text-sm" autoComplete="new-password" />
                  <input type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} placeholder="Confirm Password"
                    className="glass-input w-full text-sm" autoComplete="new-password" />
                  {passwordMsg && (
                    <p className={`text-xs m-0 ${passwordMsg.includes('success') ? 'text-uno-green' : 'text-destructive'}`}>{passwordMsg}</p>
                  )}
                  <Button variant="primary" size="sm" onClick={handleSetPassword} sound="click">save password</Button>
                </div>

                <div className="border-b border-white/5 my-5" />

                {/* API Keys section */}
                <h3 className="text-sm text-muted-foreground mb-3 flex items-center gap-1.5">
                  <Key size={14} /> API Keys
                </h3>
                <p className="text-xs text-muted-foreground mb-3">Used to connect to MCP clients (such as Claude Code) and let AI play games on your behalf</p>

                {newKeyFull && (
                  <div className="mb-3 rounded-lg border border-uno-green/30 bg-uno-green/10 p-3">
                    <p className="text-xs text-uno-green mb-2">Key Generated, please copy now (shown only once):</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 break-all rounded bg-black/30 px-2 py-1 text-xs">{newKeyFull}</code>
                      <Button size="sm" variant="secondary" onClick={handleCopyKey} sound="click">
                        <Copy size={12} /> {keyCopied ? 'Copied' : ''}
                      </Button>
                    </div>
                    {keyCopied && (
                      <button onClick={handleDismissKey} className="mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                        I have saved, close this prompt</button>
                    )}
                  </div>
                )}

                {keyError && (
                  <p className="text-xs text-destructive mb-2">{keyError}</p>
                )}

                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    placeholder="Key Name (eg: My Claude)"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    maxLength={50}
                    className="glass-input w-full text-sm flex-1"
                  />
                  <Button variant="primary" size="sm" onClick={handleCreateKey} disabled={creatingKey || !newKeyName.trim()} sound="click">
                    generate</Button>
                </div>

                {apiKeys.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    {apiKeys.map((k) => (
                      <div key={k.id} className="flex items-center justify-between rounded-lg border border-white/5 px-3 py-2">
                        <div>
                          <span className="text-sm">{k.name}</span>
                          <code className="ml-2 text-xs text-muted-foreground">{k.keyPreview}</code>
                        </div>
                        <button onClick={() => handleDeleteKey(k.id)} className="text-muted-foreground hover:text-destructive transition-colors" aria-label="Delete Key">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Close button (top-right) */}
              <button
                onClick={close}
                className="absolute top-4 right-4 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* -- Notification settings (inline, no glass-panel wrapper) -- */

const NOTIFICATION_LABELS: { key: NotificationEventType; label: string }[] = [
  { key: 'gameStart', label: 'Game starts' },
  { key: 'myTurn', label: "It's my turn to play" },
  { key: 'gameEnd', label: 'game over' },
  { key: 'kicked', label: 'kicked out of room' },
  { key: 'roomDissolved', label: 'Room disbanded' },
];

function NotificationSettingsInline() {
  const { preferences, setPreference } = useNotificationStore();
  const permission = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';

  const handleRequest = async () => {
    if (typeof Notification !== 'undefined') {
      await Notification.requestPermission();
    }
  };

  return (
    <div>
      <h3 className="text-sm text-muted-foreground mb-3 flex items-center gap-1.5">
        <Bell size={14} /> Notification settings</h3>

      {permission !== 'granted' && (
        <div className="mb-3 rounded-lg border border-accent/20 bg-accent/5 px-3 py-2">
          {permission === 'denied' ? (
            <p className="text-xs text-destructive/80">
              Notification permission has been denied. Please turn it on manually in the website settings on the left side of the browser address bar.</p>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Notification permission has not been turned on</p>
              <button onClick={handleRequest} className="text-xs font-bold text-accent hover:opacity-80 transition-opacity">
                turn on</button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {NOTIFICATION_LABELS.map(({ key, label }) => (
          <label key={key} className="flex items-center justify-between cursor-pointer">
            <span className="text-sm">{label}</span>
            <button
              type="button"
              role="switch"
              aria-checked={preferences[key]}
              onClick={() => setPreference(key, !preferences[key])}
              className={`relative h-5 w-9 rounded-full transition-colors ${preferences[key] ? 'bg-accent' : 'bg-white/15'}`}
            >
              <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${preferences[key] ? 'translate-x-4' : ''}`} />
            </button>
          </label>
        ))}
      </div>
    </div>
  );
}
